/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-14 11:07:49
 *
 *******************************************************/
// @flow

import React, { useState, useCallback } from 'react';
import { Stack, Thumbnail, Caption, DropZone, Button, Tooltip, Icon } from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

const CustomDropZone = (props: WithI18nProps) => {
  const [files, setFiles] = useState([]);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const { i18n } = props;

  const handleDropZoneDrop = useCallback(
    (dropFiles, _acceptedFiles, _rejectedFiles) => {
      const newFiles = props.allowMultiple ? [...files, ...dropFiles] : [dropFiles[0]];
      // setFiles([dropFiles[0]]);
      setFiles(files => newFiles);
      props.onSelectFile(newFiles);
    },
    [props, files]
  );

  const toggleOpenFileDialog = useCallback(() => {
    setOpenFileDialog(openFileDialog => !openFileDialog);
  }, []);

  const handleRemoveFile = useCallback(
    index => {
      setFiles(files => {
        files.splice(index, 1);
        return files;
      });
      props.onSelectFile(files);
    },
    [props, files]
  );

  const disabled = props.disabled || false;
  const type = 'file'; // Default file
  const accept = props.accept || 'text/csv'; // Default import csv
  const validFileTypes = props.fileTypes; // Viewed file in thumpnail
  const allowMultiple = props.allowMultiple || false;
  const tooltip =
    props.tooltip || i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneTooltip');

  const uploadedFiles = files.length > 0 && (
    <Stack vertical>
      {files.map((file, index) => {
        return (
          <Stack alignment="center" key={index}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={
                validFileTypes.indexOf(file.type) >= 0
                  ? window.URL.createObjectURL(file)
                  : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
              }
            />
            <div style={{ fontSize: '0.8125rem', lineHeight: '1rem' }}>
              {file.name}{' '}
              <Caption>
                {i18n.translate('AdminHTML.Common.File.size', {
                  count: file.size,
                  nt_number: i18n.formatNumber(file.size)
                })}
              </Caption>
            </div>
            <Button plain onClick={() => handleRemoveFile(index)}>
              Remove
            </Button>
          </Stack>
        );
      })}
    </Stack>
  );

  let fileInfo = [];

  files.forEach(file => {
    const { name, size } = file;
    fileInfo.push(`${name} (${size} bytes)`);
  });

  const dropzoneStyle =
    files.length > 0 && props.showDropzone
      ? { display: 'block', paddingTop: '0.9375rem' }
      : { display: 'none' };

  return (
    <div className="BC-Dropzone-Box">
      <div className="BC-Dropzone-ChooseFiles">
        <span style={{ fontWeight: 600, marginRight: '2.8125rem' }}>
          {allowMultiple
            ? i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneLabelPlural')
            : i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneLabel')}

          <span className="tooltip">
            <Tooltip
              content={<span className="tooltipContent">{tooltip}</span>}
              preferredPosition="above"
              light>
              <Icon source={QuestionMarkMajor} color="base" />
            </Tooltip>
          </span>
        </span>

        <Button size="slim" onClick={toggleOpenFileDialog}>
          {i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneContent')}
        </Button>
        {/* Display list of selected filed if dropzone is hidden */}
        {!props.showDropzone ? (
          <span style={{ paddingLeft: '5px' }}>
            {fileInfo.length > 0
              ? fileInfo.join(', ')
              : i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneEmpty')}
          </span>
        ) : (
          ''
        )}
      </div>
      <div style={dropzoneStyle}>
        <DropZone
          disabled={disabled}
          accept={accept}
          type={type}
          openFileDialog={openFileDialog}
          onDrop={handleDropZoneDrop}
          onFileDialogClose={toggleOpenFileDialog}
          onClick={() => {}}
          onDragLeave={() => {}}
          allowMultiple={allowMultiple}
          variableHeight>
          {uploadedFiles}
        </DropZone>
      </div>
    </div>
  );
};

export default withI18n()(CustomDropZone);
